// ─────────────────────────────────────────────────────────────────────────────
// notebook_model.ts — Core data model for the Grimoire notebook
// All IDs are stable: assigned once, never recomputed on reorder.
// Format: {page_short_uuid}.{kind}.{seq}
// ─────────────────────────────────────────────────────────────────────────────

// ── Primitive geometry ───────────────────────────────────────────────────────

export interface Point {
  x: number;
  y: number;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RegionBounds {
  points: Point[]; // polygon, may be freeform lasso
  bbox: BoundingBox;
}

// ── Stable identifiers ───────────────────────────────────────────────────────

export type NotebookId = string;
export type PageId = string;
export type BlockId = string;
export type StrokeId = string;
export type ArrowId = string;
export type SpellId = string;
export type AgentId = string;

// ── Ink ──────────────────────────────────────────────────────────────────────

/** Raw sample from a PointerEvent — the permanent canonical record. */
export interface StrokePoint {
  x: number;
  y: number;
  pressure: number;   // 0–1
  tiltX: number;      // degrees, -90 to 90
  tiltY: number;      // degrees, -90 to 90
  twist: number;      // degrees, 0-359
  timestamp: number;  // performance.now() ms
}

export type PenStyle = 'ink' | 'pencil' | 'marker' | 'chalk' | 'watercolor' | 'graphite' | 'infill' | 'nib' | 'eraser';
export type EraserMode = 'stroke' | 'area';

/** Which renderer paints strokes drawn with a tool. The InkLayer
 *  brush registry maps each kind to a concrete render function.
 *  - solid    — perfect-freehand outline filled (ink, marker, infill, eraser).
 *  - textured — outline + grain knockout + edge erosion (pencil, chalk, …).
 *  - wet      — radial-blob spine walk with wet-edge accumulation (watercolour).
 *  - stylus   — kinematic-aware parallel-ribbon driven by pressure / tilt /
 *               speed (Batch 5: nib).
 */
export type BrushKind = 'solid' | 'textured' | 'wet' | 'stylus';

// ── Brush profiles — per-tool rendering parameters ───────────────────────────

export interface BrushProfile {
  /** Optional explicit brush kind. When set, the InkLayer dispatches
   *  to this brush directly; when absent, the legacy flag-based
   *  inference (wetEdge → wet, grainBlend > 0 → textured, else solid)
   *  is used so existing tools render unchanged. */
  kind?: BrushKind;
  /** Perfect Freehand overrides */
  thinning: number;
  smoothing: number;
  streamline: number;
  /** Start/end taper multipliers (relative to size) */
  startTaper: number;
  endTaper: number;
  /** Opacity behaviour */
  pressureOpacity: boolean;
  baseOpacity: number;
  /** Edge texture: 0 = clean, 1 = maximum edge displacement */
  edgeNoise: number;
  /** Grain interaction: 0 = ignores paper, 1 = full paper-grain knockout */
  grainBlend: number;
  /** Edge erosion: 0 = clean borders, 1 = heavy erosion eating into stroke edges */
  edgeErosion: number;
  /** Composite operation for stroke rendering */
  composite: string;
  /** If true, overlapping segments within a single stroke accumulate opacity */
  wetEdge: boolean;
  /** Hardness: 0 = maximum feather, 1 = hard edge */
  hardness: number;
  /** Stylus / nib brush params. Tilt direction biases the ribbon
   *  asymmetry; pressure widens; slow strokes widen further (more
   *  pigment dwell). All gains 0..1 — kept on the profile so the
   *  feel can be tuned per-tool without growing ToolUserParams. */
  nibPressureGain?: number;
  nibSlowGain?: number;
  nibTiltGain?: number;
}

export const BRUSH_PROFILES: Record<PenStyle, BrushProfile> = {
  ink:        { thinning: 0.5,  smoothing: 0.5,  streamline: 0.4, startTaper: 0.5, endTaper: 2.0, pressureOpacity: false, baseOpacity: 0.92, edgeNoise: 0,    grainBlend: 0,    edgeErosion: 0,   composite: 'source-over',     wetEdge: false, hardness: 1.0 },
  pencil:     { thinning: 0.35, smoothing: 0.4,  streamline: 0.3, startTaper: 0.3, endTaper: 1.0, pressureOpacity: true,  baseOpacity: 0.55, edgeNoise: 0.15, grainBlend: 0.5,  edgeErosion: 0.3, composite: 'source-over',     wetEdge: false, hardness: 0.8 },
  graphite:   { thinning: 0.2,  smoothing: 0.35, streamline: 0.25,startTaper: 0.2, endTaper: 0.5, pressureOpacity: true,  baseOpacity: 0.4,  edgeNoise: 0.25, grainBlend: 0.65, edgeErosion: 0.45,composite: 'source-over',     wetEdge: false, hardness: 0.6 },
  // Marker now uses the 'wet' brush — the radial-blob spine walk that
  // previously drove watercolour reads more like a felt-tip marker
  // than actual watercolour. Watercolour itself stays on the same
  // brush for now; a dedicated diffusion / spread implementation lands
  // in a follow-up. Marker keeps `multiply` composite so overlapping
  // strokes darken (the classic marker-on-marker behaviour).
  marker:     { kind: 'wet', thinning: 0.0,  smoothing: 0.5,  streamline: 0.5, startTaper: 0,   endTaper: 0,   pressureOpacity: false, baseOpacity: 0.55, edgeNoise: 0,    grainBlend: 0,    edgeErosion: 0,   composite: 'multiply',        wetEdge: true, hardness: 1.0 },
  chalk:      { thinning: 0.25, smoothing: 0.3,  streamline: 0.2, startTaper: 1.5, endTaper: 0.8, pressureOpacity: true,  baseOpacity: 0.5,  edgeNoise: 0.5,  grainBlend: 0.8,  edgeErosion: 0.7, composite: 'source-over',     wetEdge: false, hardness: 0.3 },
  watercolor: { thinning: 0.1,  smoothing: 0.7,  streamline: 0.6, startTaper: 0.1, endTaper: 0.1, pressureOpacity: true,  baseOpacity: 0.25, edgeNoise: 0,    grainBlend: 0.2,  edgeErosion: 0.15,composite: 'source-over',     wetEdge: true,  hardness: 0.2 },
  infill:     { thinning: 0.0,  smoothing: 0.5,  streamline: 0.5, startTaper: 0,   endTaper: 0,   pressureOpacity: false, baseOpacity: 0.7,  edgeNoise: 0,    grainBlend: 0,    edgeErosion: 0,   composite: 'source-over',     wetEdge: false, hardness: 1.0 },
  // Nib — kinematic-aware stylus brush (Batch 5). Width is composed
  // at render time from per-point pressure + tilt + segment speed,
  // so the perfect-freehand thinning is muted and tapers are short.
  nib:        { kind: 'stylus', thinning: 0.0,  smoothing: 0.5,  streamline: 0.45,startTaper: 0.2, endTaper: 0.2, pressureOpacity: false, baseOpacity: 0.95, edgeNoise: 0,    grainBlend: 0,    edgeErosion: 0,   composite: 'source-over',     wetEdge: false, hardness: 1.0,
                nibPressureGain: 0.6, nibSlowGain: 0.45, nibTiltGain: 0.5 },
  eraser:     { thinning: 0.0,  smoothing: 0.5,  streamline: 0.5, startTaper: 0,   endTaper: 0,   pressureOpacity: false, baseOpacity: 1.0,  edgeNoise: 0,    grainBlend: 0,    edgeErosion: 0,   composite: 'destination-out', wetEdge: false, hardness: 1.0 },
};

export interface InkStyle {
  color: string;        // CSS color, default '#2c1810' (sepia)
  size: number;         // base diameter px
  opacity: number;      // 0–1
  thinning: number;     // Perfect Freehand thinning 0–1
  smoothing: number;    // Perfect Freehand smoothing 0–1
  streamline: number;   // Perfect Freehand streamline 0–1
  style: PenStyle;
}

export const DEFAULT_INK_STYLE: InkStyle = {
  color: '#2c1810',
  size: 3,
  opacity: 0.92,
  thinning: 0.5,
  smoothing: 0.5,
  streamline: 0.4,
  style: 'ink',
};

export const SPELL_INK_STYLE: InkStyle = {
  color: '#7c4daa',     // arcane violet
  size: 2.5,
  opacity: 0.8,
  thinning: 0.3,
  smoothing: 0.7,
  streamline: 0.5,
  style: 'ink',
};

export interface InkStroke {
  id: StrokeId;
  points: StrokePoint[];
  style: InkStyle;
  timestamp: number;  // committed at
  blockId?: BlockId;  // if associated with a block
  /** Page composition layer (0–5). Default 1 if absent. */
  pageLayer?: PageLayer;
  // ── Content modifiers (replaces page-layer system) ──
  /** If true, stroke is inert: cannot be erased, moved, or transformed.
   *  Replaces the old "background layer" concept. */
  locked?: boolean;
  /** Membership tag — strokes/blocks sharing a groupId move and
   *  transform together. Set by `block.group` / cleared by `content.modifier.set`. */
  groupId?: string;
  /** Astral aesthetic flag. When set, the stroke renders with an
   *  ethereal look and fades out over `ttlMs`; once
   *  `Date.now() - birth >= ttlMs` it is fully discarded by a
   *  system-origin `ink.erase`. */
  astral?: { birth: number; ttlMs: number };
}

// ── Page composition layers ──────────────────────────────────────────────────
// Layer 0: Design / background decoration (non-interactive by default)
// Layers 1–4: User working layers (all content, ink, blocks)
// Layer 5: Astral — arcane overlay with purple styling (sigil FX, arcane drawings)
// Objects without a pageLayer default to layer 1 for backward compatibility.

export type PageLayer = 0 | 1 | 2 | 3 | 4 | 5;
export const LAYER_DESIGN = 0 as PageLayer;
export const LAYER_DEFAULT = 1 as PageLayer;
export const LAYER_ASTRAL = 5 as PageLayer;
export const LAYER_COUNT = 6;

// ── Blocks ───────────────────────────────────────────────────────────────────

export type BlockKind = 'ink' | 'latex' | 'code' | 'arcane' | 'text' | 'image' | 'arrow' | 'figure';
export type BlockLayer = 0 | 1 | 2 | 3;
// Layer 0: Raw strokes (permanent)
// Layer 1: Recognized/suggested LaTeX (semi-transparent)
// Layer 2: Confirmed LaTeX (hides strokes in text-focus mode)
// Layer 3: Semantic grouping / agent export

export interface BaseBlock {
  id: BlockId;
  pageId: PageId;
  kind: BlockKind;
  bbox: BoundingBox;
  rotation: number;    // radians, 0 = upright
  selected: boolean;
  /** If true, block is inert: cannot be erased, moved, scaled, rotated,
   *  or have its strokes/text edited. Replaces the retired
   *  "background layer" concept. */
  locked: boolean;
  // ── Content modifiers (per-block, replaces page-layer system) ──
  /** Membership tag — blocks sharing a groupId move and transform
   *  together. Stamped by the `block.group` reducer. */
  groupId?: string;
  /** Astral aesthetic flag. When set, the block renders with an
   *  ethereal look and fades over `ttlMs`; once expired it is
   *  discarded by a system-origin `block.delete`. */
  astral?: { birth: number; ttlMs: number };
  createdAt: number;
  updatedAt: number;
}

export interface InkBlock extends BaseBlock {
  kind: 'ink';
  strokeIds: StrokeId[];
}

/** Math region: strokes on layer 0, latex suggestion on layer 1, confirmed on 2 */
export interface LatexBlock extends BaseBlock {
  kind: 'latex';
  strokeIds: StrokeId[];
  latexSuggestion?: string;     // layer 1 — from recognizer, unconfirmed
  latexConfirmed?: string;      // layer 2 — user/wizard confirmed
  focusMode: boolean;           // hides strokes, shows editable LaTeX
}

export type CodeLanguage = 'python' | 'typescript' | 'javascript' | 'bash' | 'other';
export type CellState = 'idle' | 'running' | 'done' | 'error';

export interface CellOutput {
  type: 'text' | 'image' | 'error' | 'stream';
  data: string;   // text or base64 image
  timestamp: number;
}

export interface CodeBlock extends BaseBlock {
  kind: 'code';
  language: CodeLanguage;
  fileRef: string;          // relative path to linked .py / .ts file
  lastRun?: number;
  state: CellState;
  outputs: CellOutput[];
}

export type ArcaneType = 'code' | 'agent' | 'wizard' | 'laura_channel' | 'ireth_channel' | 'user_sigil';
export type ArcaneState = 'sealed' | 'open' | 'active' | 'error';

export interface ArcaneChannel extends BaseBlock {
  kind: 'arcane';
  arcaneType: ArcaneType;
  state: ArcaneState;
  boundTo?: string;       // file_ref | agent_id | 'wizard'
  glyphSeed: number;      // determines which rotating glyphs appear in the void border
  ioLog: Array<{ dir: 'in' | 'out'; content: string; timestamp: number }>;
}

export interface TextBlock extends BaseBlock {
  kind: 'text';
  content: string;        // plain or markdown
  fontSize: number;
}

export type FigureEdgeType = 'line' | 'arrow' | 'curve';

export type FigureLineStyle = 'solid' | 'dashed' | 'dotted';

export interface FigureVertex {
  id: string;
  x: number;
  y: number;
}

export interface FigureEdge {
  id: string;
  from: string;            // vertex id
  to: string;              // vertex id
  type: FigureEdgeType;
  controlPoints?: Point[];
}

export interface FigureBlock extends BaseBlock {
  kind: 'figure';
  shapeKind: string;       // 'line' | 'arrow' | 'circle' | 'ellipse' | 'rectangle' | 'polygon'
  vertices: FigureVertex[];
  edges: FigureEdge[];
  center?: Point;
  rx?: number;
  ry?: number;
  penSize?: number;        // stylus thickness at creation — scales line/vertex/arrow rendering
  lineStyle?: FigureLineStyle;
  replacedStrokeIds: StrokeId[];  // strokes consumed by this recognition
}

export type Block = InkBlock | LatexBlock | CodeBlock | ArcaneChannel | TextBlock | FigureBlock;

// ── Relation Arrows ──────────────────────────────────────────────────────────

export type RelationType =
  | 'derives'
  | 'implies'
  | 'contradicts'
  | 'references'
  | 'annotates'
  | 'bound'       // closure/loop — drawn as a curved closed arrow
  | 'contains'
  | 'precedes';

export type ArrowSource = 'auto' | 'user' | 'agent' | 'wizard';

export interface RelationArrow {
  id: ArrowId;
  pageId: PageId;
  fromId: BlockId;
  toId: BlockId;
  type: RelationType;
  source: ArrowSource;
  confirmed: boolean;     // false → dotted/faint with question glyph
  label?: string;
  waypoints?: Point[];    // optional manual routing
}

// ── Page ─────────────────────────────────────────────────────────────────────

export type BookmarkTier = 'major' | 'minor';

export interface PageBookmark {
  id: string;
  label: string;
  tier: BookmarkTier;
  parentId?: string;    // major bookmark this minor belongs to
  color?: string;
}

/** Body slot — paired sub-slots so the page can layer a decorative
 *  fill (mosaic / watermark / template) under a writing-guide
 *  pattern (lines / dots / grid). */
export interface PageBodyDecorations {
  /** Writing guide — drawn on top of graphics, beneath ink. */
  justification?: string;
  /** Decorative fill — drawn beneath justification at low alpha. */
  graphics?: string;
}

/** Margins slot — paired sub-slots so the page can pair a themed
 *  ornament (greek columns, vines, arches) with functional marks
 *  (rules, hash, stipple). Utility lives snug to the body in the
 *  gutter; graphics occupies the margin band itself. */
export interface PageMarginDecorations {
  /** Functional marks anchored to the gutter. */
  utility?: string;
  /** Themed ornament centred in / spanning the margin band. */
  graphics?: string;
}

/** Page-scoped decorative styles. Each slot holds either a registry id
 *  or a nested category map. Purely per-page, never inherited from a
 *  section. The renderer resolves ids at draw time so the catalogue
 *  can grow without schema churn. */
export interface PageDecorations {
  /** Body sub-slots — justification + graphics. */
  body?: PageBodyDecorations;
  /** Margins sub-slots — utility + graphics. */
  margins?: PageMarginDecorations;
  /** Frame around the body content rect — delimits the writing area
   *  (not the page edge). Plain / Simple / Thick / Single-left / … */
  frame?: string;
}

export interface Page {
  id: PageId;
  index: number;        // 0-based, 0 = context index, last = settings
  title: string;
  color?: string;       // tab accent color (from PAGE_PALETTE)
  sectionId?: string;   // which section this page belongs to
  isSectionHeader?: boolean;  // true for auto-created section header pages
  blocks: Map<BlockId, Block>;
  arrows: Map<ArrowId, RelationArrow>;
  bookmark?: PageBookmark;
  decorations?: PageDecorations;
  densityLevel: number;   // 0–1, drives the "page filling up" visual feedback
  createdAt: number;
  updatedAt: number;
}

// ── Sections ─────────────────────────────────────────────────────────────────

export type SectionId = string;
/** Hidden system kinds never appear in the main navigation UI. */
export type SectionKind = 'journal' | 'adventure' | 'fold' | 'collection' | 'chapter';

/** True for section kinds that should be invisible to the main navigation. */
export function isHiddenSectionKind(kind: SectionKind | undefined): boolean {
  return kind === 'fold' || kind === 'adventure';
}

export interface Section {
  id: SectionId;
  title: string;          // user-drawn title (OCR'd or placeholder)
  description: string;    // section description text
  color: string;          // accent color from SECTION_PALETTE
  pageIds: PageId[];      // ordered pages within this section
  headerPageId: PageId;   // ID of the mandatory header page
  kind?: SectionKind;     // undefined = 'journal' (backward compat)
  /** If false, color and title are locked (built-in sections). Default true. */
  customizable?: boolean;
  createdAt: number;
}

export function makeSectionId(): SectionId {
  return `sec_${shortId()}`;
}

// ── Notebook ─────────────────────────────────────────────────────────────────

export interface NotebookMeta {
  id: NotebookId;
  title: string;
  folderPath: string;
  createdAt: number;
  updatedAt: number;
  coverSeed: number;    // generative cover art seed
}

export interface Notebook {
  meta: NotebookMeta;
  pages: Page[];
  currentPageIndex: number;
  focusedRegion?: RegionBounds | BlockId;
  activeBlockId?: BlockId;
  _seqCounters: Record<string, number>;  // per-page block sequence counters
}

// ── ID factory ───────────────────────────────────────────────────────────────

let _globalSeq = 0;

function shortId(): string {
  return (Date.now().toString(36) + (++_globalSeq).toString(36)).slice(-8);
}

export function makePageId(): PageId {
  return `pg_${shortId()}`;
}

export function makeBlockId(pageId: PageId, kind: BlockKind, notebook: Notebook): BlockId {
  const key = `${pageId}.${kind}`;
  notebook._seqCounters[key] = (notebook._seqCounters[key] ?? 0) + 1;
  return `${pageId}.${kind}.${notebook._seqCounters[key]}`;
}

export function makeStrokeId(): StrokeId {
  return `sk_${shortId()}`;
}

export function makeArrowId(): ArrowId {
  return `ar_${shortId()}`;
}

// ── Page factory ─────────────────────────────────────────────────────────────

export function createPage(index: number, title: string): Page {
  return {
    id: makePageId(),
    index,
    title,
    blocks: new Map(),
    arrows: new Map(),
    densityLevel: 0,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function createNotebook(title: string, folderPath: string): Notebook {
  const settingsPage = createPage(0, 'Settings');

  return {
    meta: {
      id: `nb_${shortId()}`,
      title,
      folderPath,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      coverSeed: Math.floor(Math.random() * 0xffffff),
    },
    pages: [settingsPage],
    currentPageIndex: 0,
    _seqCounters: {},
  };
}
